<template>
  <!-- Static ionicons passthrough — no async loading needed -->
  <ion-icon v-if="icon" :icon="icon" />
  <!-- Async API icon -->
  <ion-icon v-else-if="isLoaded !== null" :icon="iconString" />
  <!-- Fallback until the API icon is available; default: hourglass -->
  <slot v-else name="fallback">
    <ApiIcon :icon="hourglassOutline" />
  </slot>
</template>

<script setup lang="ts">
import { computed, defineComponent, watch } from "vue";
import { IonIcon } from "@ionic/vue";
import { hourglassOutline } from "ionicons/icons";
import type { IconName, IconSet, IconStyle } from "@/models/api";
import { useApiStore } from "@/stores/api";
import ApiIcon from "@/components/ApiIcon.vue";

const props = withDefaults(
  defineProps<{
    type?: IconSet | null;
    name?: IconName | null;
    iconStyle?: IconStyle | null;
    /** Static ionicons value (e.g. `hourglassOutline`). Bypasses async fetch. */
    icon?: string;
  }>(),
  {
    type: null,
    name: null,
    iconStyle: null,
  },
);

const api = useApiStore();

defineComponent({
  name: "ApiIcon",
});

const isLoaded = computed(() => {
  const type = props.type;
  const name = props.name;
  const style = props.iconStyle;

  if (
    type === null ||
    type === undefined ||
    name === null ||
    name === undefined ||
    style === null ||
    style === undefined
  ) {
    return null;
  }

  return api.iconSvgMeta(type, name, style);
});

// Side effects don't belong in a computed: request the icon from a watcher instead.
// The store de-duplicates, queues and rate-limits, the meta shows up reactively once loaded.
watch(
  () => [props.type, props.name, props.iconStyle, isLoaded.value] as const,
  ([type, name, style, meta]) => {
    if (type && name && style && meta === null) {
      // noinspection TypeScriptValidateJSTypes
      api.fetchIcon({ type, name, style });
    }
  },
  { immediate: true },
);

const iconString = computed(() => {
  const meta = isLoaded.value;
  if (!meta) {
    return hourglassOutline;
  }
  return `data:image/svg+xml;utf8,${meta.raw}`;
});
</script>

<style scoped></style>
